/**
 * Is this fare option really the airline's own fare for THESE flights?
 *
 * Plain JS with no runtime APIs, so the fare-options page, the review page,
 * the Express server and the edge checkouts (Deno) all apply one rule.
 *
 * Why (audit 2026-10-27): ticketed trips lost $3,391 net on fares that were
 * never the airline's own price for the flights we sold.
 *   - OTA legs. A separate-tickets combo is two purchases, and one of them was
 *     an online travel agency: "Ovago, American" (DS68IN), "FlightHub, Korean
 *     Air", "Frontier, eDreams". The page trusted Google's `airline: true`,
 *     which the combined option copied from its outbound leg, and accepted
 *     any seller string that merely CONTAINED a carrier name.
 *   - Other flights. The fare saved with the trip was for different flights
 *     than the ones booked: AKZPQY's return fare was UA 2657 for a DL 2879
 *     flight, MDD3YA's AA 1918 for AA 2916, 4FLGZU's Southwest legs for Delta
 *     flights. Checkout only checked the return's route, never the fare's
 *     flight numbers.
 *
 * The rule (founder, 2026-10-28): separate tickets stay on the site, but every
 * ticket in a fare must be sold by the airline itself, never a third party,
 * and the fare's flight numbers must be the flights shown.
 *
 * checkFareOptionIntegrity({ bookingOption, slices }) → {
 *   ok,                // true when the fare may be sold for these flights
 *   separateTickets,   // the fare is two purchases (departing + returning)
 *   problems: [{ code, leg, ... }],
 * }
 * Problem codes:
 *   no_fare                 no fare option at all (the caller decides what that means)
 *   seller_not_airline      a seller on a ticket isn't an airline flying it (an OTA, or unknown)
 *   flights_mismatch        the ticket's flight numbers aren't the flights shown
 *   flights_unverified      a separate ticket names no flights, so it can't be matched
 *   fare_for_other_flights  the fare was picked for other flights (its `_fare_for` stamp)
 *
 * A codeshare is still the same flight: Delta sells KE 18 as "DL 7836", and
 * 8% of real single-purchase fares carry one. The same airline under another
 * number is always another flight. A different carrier is accepted as a
 * codeshare:
 *   - on a fare fresh from Google for this itinerary (`fresh`: the
 *     fare-options page), or one whose `_fare_for` stamp proves it was picked
 *     for exactly these flights;
 *   - otherwise (a separate ticket, or a saved fare with no stamp) only
 *     between known partner airlines.
 *
 * `_fare_for` ({ flights: "AA100,AA200|AA300", booking_token }) is stamped on
 * the fare when the customer picks it (fareForStamp). A saved fare whose stamp
 * names other flights was carried onto this itinerary from another one: 29%
 * of carts did that before 2026-10-28 (the fare-options page wrote a newly
 * opened flight into the previous cart, which kept its fare).
 */
import { normalizeAirlineName, resolveAirlineIata } from './airline-iata.js';

export const FARE_INTEGRITY_CODE = 'FARE_NOT_FOR_THESE_FLIGHTS';

/** Online travel agencies Google lists as sellers. Any seller that isn't an
 *  airline flying the ticket is refused anyway; this list only makes sure an
 *  agency whose name contains an airline's never slips through. */
export const KNOWN_TRAVEL_AGENCIES = Object.freeze([
  'edreams', 'ovago', 'flighthub', 'justfly', 'gotogate', 'mytrip', 'checknfly', 'check n fly',
  'kiwi.com', 'kiwi', 'trip.com', 'expedia', 'priceline', 'cheapoair', 'onetravel', 'opodo',
  'budgetair', 'travelgenio', 'booking.com', 'kayak', 'orbitz', 'hopper', 'lastminute',
  'flightnetwork', 'business-class.com', 'easemytrip', 'ease my trip', 'travelocity', 'hotwire',
  'skyscanner', 'tripsta', 'fareboom', 'asaptickets', 'studentuniverse', 'flightsmojo',
  'faremart', 'travel2be', 'vayama', 'smartfares', 'wowfare', 'airfordable', 'momondo',
  'makemytrip', 'yatra', 'cleartrip', 'ixigo', 'despegar', 'decolar', 'webjet', 'flight centre',
  'flightcentre', 'travelup', 'travel trolley', 'travelstart', 'lol.travel', 'aviasales',
  'tripadvisor', 'agoda', 'traveloka', 'tiket.com', 'fly.com', 'cheapflights', 'cheaptickets',
  'kissandfly', 'flyfar', 'airgorilla', 'farecompare', 'flightsforless', 'wego', 'adam vacations',
]);

/** Carriers that sell each other's flights under their own numbers. Only used
 *  where a codeshare needs vouching for (a separate ticket, or a saved fare).
 *  Alliances plus the joint ventures we see in Defera's traffic. */
const PARTNER_GROUPS = [
  // Star Alliance + group airlines
  ['UA', 'LH', 'AC', 'NH', 'SQ', 'TK', 'LX', 'OS', 'SN', 'TP', 'LO', 'SA', 'ET', 'MS', 'A3', 'OU', 'OZ', 'BR', 'CA', 'AI', 'AV', 'CM', 'NZ', 'TG', 'ZH', 'EN', 'EW', '4Y', 'VL', 'WK', 'QK', 'RV', 'ZX', 'AZ'],
  // SkyTeam + group airlines
  ['DL', 'AF', 'KL', 'KE', 'AM', 'VS', 'VN', 'MU', 'CI', 'GA', 'KQ', 'ME', 'MF', 'RO', 'SV', 'UX', 'AR', 'SK', 'HV', 'A5', 'WA', 'AZ'],
  // oneworld + group airlines (Hawaiian sells Alaska's flights since the merger)
  ['AA', 'AS', 'HA', 'BA', 'IB', 'AY', 'CX', 'JL', 'QF', 'QR', 'MH', 'RJ', 'UL', 'FJ', 'AT', 'WY', 'MQ', 'OH', 'PT', 'CJ', 'I2', 'YW', 'NU', 'EI', 'VY'],
];
const PARTNER_PAIRS = [
  ['AC', 'KE'], ['AF', 'OU'], ['AA', 'PR'], ['AC', 'EY'], ['AA', 'EY'], ['DL', 'LA'], ['DL', 'WS'], ['DL', 'VA'], ['DL', 'G3'], ['AA', 'G3'], ['UA', 'VA'], ['UA', 'EI'],
  ['EK', 'FZ'], ['EK', 'QF'], ['QF', 'JQ'], ['AS', 'EK'], ['AS', 'KE'], ['AS', 'LA'], ['AS', 'SQ'],
  ['B6', 'EI'], ['B6', 'EK'], ['B6', 'QR'], ['B6', 'TP'], ['B6', 'FI'], ['B6', 'TK'], ['AM', 'LA'],
  ['UA', 'AD'], ['AA', 'AD'], ['UA', 'LA'], ['G3', 'AV'], ['AA', 'KE'], ['EY', 'B6'],
];

export function arePartnerCarriers(a, b) {
  const x = String(a || '').toUpperCase();
  const y = String(b || '').toUpperCase();
  if (!x || !y) return false;
  if (x === y) return true;
  if (PARTNER_GROUPS.some((g) => g.includes(x) && g.includes(y))) return true;
  return PARTNER_PAIRS.some(([p, q]) => (p === x && q === y) || (p === y && q === x));
}

const PLACEHOLDER_CODES = new Set(['XX', '']);

function carrierCode(value) {
  const c = String(value || '').trim().toUpperCase();
  return /^[A-Z0-9]{2}$/.test(c) && !PLACEHOLDER_CODES.has(c) ? c : null;
}

/** "AA 1659", "aa1659", "AA 01659" → "AA1659". A bare number takes `carrier`. */
export function normalizeFlightNumber(value, carrier = null) {
  const raw = String(value ?? '').toUpperCase().replace(/[\s-]+/g, '');
  if (!raw) return null;
  if (/^\d{1,5}$/.test(raw)) {
    const c = carrierCode(carrier);
    return c ? `${c}${Number(raw)}` : null;
  }
  const m = raw.match(/^([A-Z0-9]{2})(\d{1,5})$/);
  if (!m || PLACEHOLDER_CODES.has(m[1])) return null;
  return `${m[1]}${Number(m[2])}`;
}

const carrierOf = (flightNumber) => (flightNumber ? flightNumber.slice(0, 2) : null);

function segmentFacts(seg) {
  const mc = carrierCode(seg?.marketing_carrier?.iata_code);
  const oc = carrierCode(seg?.operating_carrier?.iata_code) || mc;
  const numbers = [
    normalizeFlightNumber(seg?.marketing_carrier_flight_number, mc),
    normalizeFlightNumber(seg?.operating_carrier_flight_number, oc),
    normalizeFlightNumber(seg?.flight_number, mc),
  ].filter(Boolean);
  const codes = new Set([mc, oc, ...numbers.map(carrierOf)].filter(Boolean));
  const names = [seg?.marketing_carrier?.name, seg?.operating_carrier?.name, seg?.airline]
    .map(normalizeAirlineName)
    .filter((n) => n && n !== 'unknown' && n !== 'airline');
  return { numbers, codes, names, primary: numbers[0] || null };
}

/** The flights of an itinerary as "AA100,AA200|AA300": slices by "|". */
export function itineraryFlightKey(slices) {
  if (!Array.isArray(slices) || slices.length === 0) return '';
  return slices
    .map((slice) => (slice?.segments || []).map((seg) => segmentFacts(seg).primary || '?').join(','))
    .join('|');
}

/** Google's `selected_flights` blocks as slices this module can read. */
export function slicesFromSelectedFlights(selectedFlights) {
  if (!Array.isArray(selectedFlights)) return [];
  return selectedFlights.map((block) => ({
    segments: (block?.flights || []).map((f) => ({
      marketing_carrier: { name: f?.airline || null, iata_code: carrierOf(normalizeFlightNumber(f?.flight_number)) },
      marketing_carrier_flight_number: f?.flight_number || null,
    })),
  }));
}

/** The record stamped on a fare when the customer picks it, so a later
 *  checkout can prove the fare is for these flights. */
export function fareForStamp(slices, bookingToken = null) {
  return {
    flights: itineraryFlightKey(slices),
    ...(bookingToken ? { booking_token: bookingToken } : {}),
  };
}

/** "Ovago, American" → ["Ovago", "American"]. */
export function sellerNames(bookWith) {
  return String(bookWith || '')
    .split(/\s*(?:,|\+|;|\||\s&\s|\s\/\s)\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const stripSuffix = (n) => n.replace(/\s+(airlines?|air\s?lines?|airways?|aviation|air)$/, '');

function isTravelAgency(normalized) {
  return KNOWN_TRAVEL_AGENCIES.some((a) => normalized === a || normalized.includes(a));
}

/** `inner` appears in `outer` as whole words (at least 4 letters). */
function containsWords(outer, inner) {
  if (!inner || inner.length < 4) return false;
  return (` ${outer} `).includes(` ${inner} `);
}

function sellerIsAirlineOnLeg(seller, leg) {
  const n = normalizeAirlineName(seller);
  if (!n || isTravelAgency(n)) return false;
  const code = resolveAirlineIata(seller) || carrierCode(/^[a-z0-9]{2}$/.test(n) ? n : null);
  if (code && [...leg.codes].some((c) => arePartnerCarriers(code, c))) return true;
  const sn = stripSuffix(n);
  return leg.names.some((name) => {
    const cn = stripSuffix(name);
    return cn === sn || containsWords(sn, cn) || containsWords(cn, sn);
  });
}

/** The tickets a fare option is made of, each with the slices it flies. */
function ticketsOf(bookingOption, sliceCount) {
  const separate = !!(bookingOption?.departing && bookingOption?.returning);
  if (separate) {
    return {
      separate,
      tickets: [
        { leg: 'departing', option: bookingOption.departing, slices: [0] },
        { leg: 'returning', option: bookingOption.returning, slices: Array.from({ length: Math.max(0, sliceCount - 1) }, (_, i) => i + 1) },
      ],
    };
  }
  const option = bookingOption?.together || bookingOption?.departing || null;
  return {
    separate,
    tickets: option ? [{ leg: 'together', option, slices: Array.from({ length: sliceCount }, (_, i) => i) }] : [],
  };
}

/** One flight number that covers two segments (a through flight with a stop)
 *  can be listed once or twice: compare the runs, not the repeats. */
function withoutRepeats(list, keyOf) {
  return list.filter((item, i) => i === 0 || keyOf(item) !== keyOf(list[i - 1]));
}

function checkTicketFlights(ticket, allSegments, { strictCodeshares, strict }) {
  const fare = withoutRepeats(
    (Array.isArray(ticket.option?.marketed_as) ? ticket.option.marketed_as : [])
      .map((n) => normalizeFlightNumber(n))
      .filter(Boolean),
    (n) => n,
  );
  if (fare.length === 0) {
    return strict ? { code: 'flights_unverified', leg: ticket.leg } : null;
  }
  const segments = withoutRepeats(allSegments, (s) => s.primary || Symbol('unnumbered'));
  const shown = segments.map((s) => s.primary || '?').join(',');
  if (fare.length !== segments.length) {
    return { code: 'flights_mismatch', leg: ticket.leg, fare: fare.join(','), flights: shown };
  }
  for (let i = 0; i < fare.length; i += 1) {
    const seg = segments[i];
    if (seg.numbers.includes(fare[i])) continue;
    const fareCarrier = carrierOf(fare[i]);
    // The same airline with another number is another flight.
    const sameAirline = seg.codes.has(fareCarrier);
    const codeshare = !sameAirline
      && (!strictCodeshares || [...seg.codes].some((c) => arePartnerCarriers(fareCarrier, c)));
    if (!codeshare) {
      return { code: 'flights_mismatch', leg: ticket.leg, fare: fare.join(','), flights: shown };
    }
  }
  return null;
}

/**
 * @param {object} p
 * @param {object} p.bookingOption  the fare option ({ together } or { departing, returning }, as Google sends it)
 * @param {object[]} p.slices       the flights being sold, Duffel-shaped slices with segments
 * @param {boolean} [p.fresh]      the fare came from Google for these flights just now (the fare-options page)
 */
export function checkFareOptionIntegrity({ bookingOption, slices, fresh = false } = {}) {
  const sliceList = Array.isArray(slices) ? slices : [];
  if (!bookingOption || typeof bookingOption !== 'object') {
    return { ok: false, separateTickets: false, problems: [{ code: 'no_fare', leg: null }] };
  }
  const { separate, tickets } = ticketsOf(bookingOption, sliceList.length);
  const problems = [];
  if (tickets.length === 0) problems.push({ code: 'no_fare', leg: null });

  const segmentsBySlice = sliceList.map((s) => (s?.segments || []).map(segmentFacts));
  const stamp = bookingOption._fare_for && typeof bookingOption._fare_for.flights === 'string'
    ? bookingOption._fare_for
    : null;
  // Codeshares need a partner airline unless the pairing is proven (see top).
  const strictCodeshares = separate || (!fresh && !stamp);

  for (const ticket of tickets) {
    const segments = ticket.slices.flatMap((i) => segmentsBySlice[i] || []);
    if (segments.length === 0) {
      problems.push({ code: 'flights_mismatch', leg: ticket.leg, fare: (ticket.option?.marketed_as || []).join(','), flights: '' });
      continue;
    }
    const fareNumbers = (ticket.option?.marketed_as || []).map((n) => normalizeFlightNumber(n)).filter(Boolean);
    const leg = {
      codes: new Set([...segments.flatMap((s) => [...s.codes]), ...fareNumbers.map(carrierOf)]),
      names: segments.flatMap((s) => s.names),
    };
    const sellers = sellerNames(ticket.option?.book_with);
    if (sellers.length === 0) {
      problems.push({ code: 'seller_not_airline', leg: ticket.leg, seller: null });
    }
    for (const seller of sellers) {
      if (!sellerIsAirlineOnLeg(seller, leg)) {
        problems.push({ code: 'seller_not_airline', leg: ticket.leg, seller });
      }
    }
    const flightProblem = checkTicketFlights(ticket, segments, { strict: separate, strictCodeshares });
    if (flightProblem) problems.push(flightProblem);
  }

  // Separate tickets are two airline purchases. The combined view's seller
  // list must hold airlines only, too (Google writes "Frontier, eDreams").
  if (separate && bookingOption.together?.book_with) {
    const all = {
      codes: new Set(segmentsBySlice.flat().flatMap((s) => [...s.codes])),
      names: segmentsBySlice.flat().flatMap((s) => s.names),
    };
    for (const seller of sellerNames(bookingOption.together.book_with)) {
      if (!sellerIsAirlineOnLeg(seller, all) && !problems.some((p) => p.seller === seller)) {
        problems.push({ code: 'seller_not_airline', leg: 'together', seller });
      }
    }
  }

  if (stamp) {
    const key = itineraryFlightKey(sliceList);
    if (stamp.flights !== key) {
      problems.push({ code: 'fare_for_other_flights', leg: null, fare: stamp.flights, flights: key });
    }
  }

  return { ok: problems.length === 0, separateTickets: separate, problems };
}

/** The customer-facing sentence for a refused fare. */
export const FARE_INTEGRITY_MESSAGE = 'This fare is no longer available for these flights. Please pick your fare again.';
